import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { Palette, Image as ImageIcon, Upload, Trash2, Layers, Square } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Modal from "@/components/Modal";

const GALLERY = [
  "https://images.unsplash.com/photo-1579546929518-9e396f3cc809?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
  "https://images.unsplash.com/photo-1635776062360-af423602aff3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
  "https://images.unsplash.com/photo-1604079628040-94301bb21b91?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
  "https://images.unsplash.com/photo-1626169572088-5959632d71e5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
  "https://images.unsplash.com/photo-1774816067482-6bee52f6207e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
  "https://images.unsplash.com/photo-1771100065000-62bc31315aca?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
];
const PRESETS = ["#f9f9f7", "#0d1730", "#1B4332", "#2EC4B6", "#E37B5B", "#264653", "#6D597A", "#F4A261"];
const THEMES = [
  { name: "Midnight", type: "color", value: "#0d1730", text: "light" },
  { name: "Forest", type: "color", value: "#1B4332", text: "light" },
  { name: "Slate", type: "color", value: "#264653", text: "light" },
  { name: "Plum", type: "color", value: "#6D597A", text: "light" },
  { name: "Cream", type: "color", value: "#f9f9f7", text: "dark" },
  { name: "Sand", type: "color", value: "#F4A261", text: "dark" },
];
const SCOPE_LABELS = { personal: "Personal", business: "Business", snapbudget: "SnapBudget", all: "This tab" };

function downscale(file, maxW = 1920, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    const reader = new FileReader();
    reader.onload = () => { img.src = reader.result; };
    reader.onerror = reject;
    img.onload = () => {
      const scale = Math.min(1, maxW / img.width);
      const c = document.createElement("canvas");
      c.width = Math.round(img.width * scale);
      c.height = Math.round(img.height * scale);
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      resolve(c.toDataURL("image/jpeg", quality));
    };
    img.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function BackgroundSettings({ open, onClose, scope = "all" }) {
  const { user, refreshUser } = useAuth();
  const backgrounds = user?.backgrounds || {};
  // target: "tab" applies to this tab's scope, "all" applies across every tab
  const [target, setTarget] = useState(scope === "all" ? "all" : "tab");
  const activeScope = target === "all" ? "all" : scope;
  const current = backgrounds[activeScope] || { type: "none", value: "", opacity: 100 };
  const [opacity, setOpacity] = useState(current.opacity ?? 100);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();
  const opacityTimer = useRef(null);

  useEffect(() => {
    if (open) setTarget(scope === "all" ? "all" : "tab");
  }, [open, scope]);

  useEffect(() => {
    const bg = backgrounds[activeScope];
    setOpacity(bg?.opacity ?? 100);
  }, [activeScope, open]); // eslint-disable-line react-hooks/exhaustive-deps

  const apply = async (type, value, op = opacity, textMode) => {
    setBusy(true);
    try {
      await api.put("/prefs/background", { type, value, opacity: op, scope: activeScope, text_mode: textMode ?? current.text_mode });
      await refreshUser();
      if (type === "none") toast.success("Background reset");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const setTextMode = async (mode) => {
    const bg = backgrounds[activeScope];
    if (!bg?.type || bg.type === "none") { toast.error("Pick a background first"); return; }
    await apply(bg.type, bg.value, opacity, mode);
  };

  const customThemes = user?.theme_presets || [];
  const applyTheme = (t) => apply(t.type, t.value, opacity, t.text_mode);
  const saveTheme = async () => {
    const bg = backgrounds[activeScope];
    if (!bg?.type || bg.type === "none") { toast.error("Pick a background first"); return; }
    const name = window.prompt("Name this theme", "My theme");
    if (!name) return;
    try { await api.post("/theme-presets", { name, type: bg.type, value: bg.value, text_mode: bg.text_mode || "dark" }); await refreshUser(); toast.success("Theme saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delTheme = async (id) => { try { await api.delete(`/theme-presets/${id}`); await refreshUser(); } catch {} };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const dataUrl = await downscale(file);
      const { data } = await api.post("/prefs/background/upload", { data_url: dataUrl, scope: activeScope });
      await api.put("/prefs/background", { type: "image", value: data.url, opacity, scope: activeScope });
      await refreshUser();
      toast.success("Background updated");
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail) || "Upload failed"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const onOpacity = (v) => {
    setOpacity(v);
    const bg = backgrounds[activeScope];
    if (!(bg?.type && bg.type !== "none")) return;
    if (opacityTimer.current) clearTimeout(opacityTimer.current);
    opacityTimer.current = setTimeout(() => apply(bg.type, bg.value, v), 350);
  };

  const thisTabLabel = SCOPE_LABELS[scope] || "This tab";

  return (
    <Modal open={open} onClose={onClose} testid="background-settings" subtitle="Personalize" title="Background">
      <div className="space-y-6">
        {/* Apply target */}
        {scope !== "all" && (
          <div>
            <p className="label-mono text-[var(--text-2)] mb-3">Apply to</p>
            <div className="grid grid-cols-2 gap-2">
              <button
                data-testid="bg-target-tab"
                onClick={() => setTarget("tab")}
                className={`flex items-center justify-center gap-2 h-11 border text-sm transition-colors ${target === "tab" ? "border-[var(--accent)] text-[var(--accent)] bg-[var(--accent)]/5" : "border-[var(--line)] text-[var(--text-2)] hover:border-[var(--accent)]"}`}
              >
                <Square className="w-4 h-4" /> This tab ({thisTabLabel})
              </button>
              <button
                data-testid="bg-target-all"
                onClick={() => setTarget("all")}
                className={`flex items-center justify-center gap-2 h-11 border text-sm transition-colors ${target === "all" ? "border-[var(--accent)] text-[var(--accent)] bg-[var(--accent)]/5" : "border-[var(--line)] text-[var(--text-2)] hover:border-[var(--accent)]"}`}
              >
                <Layers className="w-4 h-4" /> All tabs
              </button>
            </div>
            <p className="mt-2 text-xs text-[var(--text-2)]">
              {target === "tab"
                ? `Changes affect the ${thisTabLabel} tab only.`
                : "Changes apply everywhere. Tabs with their own background keep it."}
            </p>
          </div>
        )}

        {/* Theme presets */}
        <div>
          <p className="label-mono text-[var(--text-2)] flex items-center gap-2 mb-3"><Layers className="w-4 h-4" /> Theme presets</p>
          <div className="grid grid-cols-3 gap-2" data-testid="bg-themes">
            {THEMES.map((t) => (
              <button key={t.name} data-testid={`bg-theme-${t.name}`} onClick={() => apply("color", t.value, opacity, t.text)} style={{ backgroundColor: t.value }} className="h-14 rounded-lg border border-[var(--line)] hover:ring-2 hover:ring-[var(--accent)] transition-all flex items-end p-2">
                <span className="text-[11px] font-medium" style={{ color: t.text === "light" ? "#f5f5f2" : "#1a1a18" }}>{t.name}</span>
              </button>
            ))}
            {customThemes.map((t) => (
              <div key={t.id} className="relative">
                <button data-testid={`bg-custom-theme-${t.id}`} onClick={() => applyTheme(t)} style={{ backgroundColor: t.type === "color" ? t.value : "#2A4B46" }} className="w-full h-14 rounded-lg border border-[var(--line)] hover:ring-2 hover:ring-[var(--accent)] transition-all flex items-end p-2">
                  <span className="text-[11px] font-medium truncate" style={{ color: t.text_mode === "light" ? "#f5f5f2" : "#1a1a18" }}>{t.name}</span>
                </button>
                <button data-testid={`bg-custom-theme-del-${t.id}`} onClick={() => delTheme(t.id)} className="absolute -top-1.5 -right-1.5 w-5 h-5 grid place-items-center rounded-full bg-white border border-[var(--line)] text-[var(--text-2)] hover:text-[var(--danger)]"><Trash2 className="w-3 h-3" /></button>
              </div>
            ))}
          </div>
          <button data-testid="bg-save-theme" onClick={saveTheme} className="mt-2 w-full h-9 flex items-center justify-center gap-2 border border-[var(--line)] text-sm text-[var(--text-2)] hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors"><Layers className="w-3.5 h-3.5" /> Save current as theme</button>
        </div>

        {/* Text readability toggle */}
        <div>
          <p className="label-mono text-[var(--text-2)] mb-3">Text color</p>
          <div className="grid grid-cols-2 gap-2" data-testid="bg-text-toggle">
            <button data-testid="bg-text-dark" onClick={() => setTextMode("dark")} className={`h-11 border text-sm transition-colors ${current.text_mode === "dark" ? "border-[var(--accent)] text-[var(--accent)] bg-[var(--accent)]/5" : "border-[var(--line)] text-[var(--text-2)] hover:border-[var(--accent)]"}`}>Dark text</button>
            <button data-testid="bg-text-light" onClick={() => setTextMode("light")} className={`h-11 border text-sm transition-colors ${current.text_mode === "light" ? "border-[var(--accent)] text-[var(--accent)] bg-[var(--accent)]/5" : "border-[var(--line)] text-[var(--text-2)] hover:border-[var(--accent)]"}`}>Light text</button>
          </div>
          <p className="mt-2 text-xs text-[var(--text-2)]">Keeps text readable over any background image or color.</p>
        </div>

        {/* Color */}
        <div>
          <p className="label-mono text-[var(--text-2)] flex items-center gap-2 mb-3"><Palette className="w-4 h-4" /> Solid color</p>
          <div className="flex items-center gap-2 flex-wrap">
            {PRESETS.map((c) => (
              <button key={c} data-testid={`bg-color-${c.replace("#", "")}`} onClick={() => apply("color", c)} style={{ backgroundColor: c }} className="w-8 h-8 rounded-full border border-[var(--line)] hover:scale-110 transition-transform" />
            ))}
            <label className="w-8 h-8 rounded-full border border-dashed border-[var(--text-2)] grid place-items-center cursor-pointer overflow-hidden relative">
              <Palette className="w-4 h-4 text-[var(--text-2)]" />
              <input data-testid="bg-color-custom" type="color" onChange={(e) => apply("color", e.target.value)} className="absolute inset-0 opacity-0 cursor-pointer" />
            </label>
          </div>
        </div>

        {/* Gallery */}
        <div>
          <p className="label-mono text-[var(--text-2)] flex items-center gap-2 mb-3"><ImageIcon className="w-4 h-4" /> Photo gallery</p>
          <div className="grid grid-cols-3 gap-2">
            {GALLERY.map((url, i) => (
              <button key={url} data-testid={`bg-gallery-${i}`} onClick={() => apply("image", url)} className="aspect-video rounded-lg overflow-hidden border border-[var(--line)] hover:ring-2 hover:ring-[var(--accent)] transition-all">
                <img src={`${url}&h=200`} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>

        {/* Upload */}
        <div>
          <p className="label-mono text-[var(--text-2)] flex items-center gap-2 mb-3"><Upload className="w-4 h-4" /> Your photo</p>
          <button data-testid="bg-upload-btn" onClick={() => fileRef.current?.click()} disabled={busy} className="w-full h-11 border border-dashed border-[var(--text-2)] text-sm hover:border-[var(--accent)] transition-colors disabled:opacity-50">
            {busy ? "Working…" : "Upload an image"}
          </button>
          <input ref={fileRef} data-testid="bg-upload-input" type="file" accept="image/*" onChange={onFile} className="hidden" />
        </div>

        {/* Opacity */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="label-mono text-[var(--text-2)]">Background visibility</span>
            <span className="text-sm font-mono" data-testid="bg-opacity-value">{opacity}%</span>
          </div>
          <input data-testid="bg-opacity" type="range" min="0" max="100" value={opacity} onChange={(e) => onOpacity(Number(e.target.value))} className="w-full accent-[var(--accent)]" />
        </div>

        <button data-testid="bg-reset" onClick={() => apply("none", "")} disabled={busy} className="w-full h-11 flex items-center justify-center gap-2 border border-[var(--line)] text-sm hover:bg-[#f9f9f7] transition-colors disabled:opacity-50">
          <Trash2 className="w-4 h-4" /> Remove {target === "all" ? "shared" : thisTabLabel} background
        </button>
      </div>
    </Modal>
  );
}
